import { v4 as uuid } from "uuid";
import { isNotUndefined, isUndefined, partition } from "../utils/dictionaries";
import { WorldState } from "./world";

export enum FactionType {
  FAMILY = "FAMILY",
  POS = "POS",
  GUILD = "GUILD",
  SOCIETY = "SOCIETY",
}

export type FactionMembership = {
  factionId: string;
  factionType: FactionType;
  factionName: string;
  influence: number;
};

export type FactionAgent = {
  factions: FactionMembership[];
};

export type Faction<T extends FactionType> = {
  id: string;
  name: string;
  type: T;
  leader: string | undefined;
};

const hasFactionType = (type: FactionType) => (f: FactionAgent) =>
  f.factions.findIndex((f) => f.factionType === type) > -1;

export type MetaItem = {
  key: string;
  from: number;
  to: number;
  quantity: number;
};

export class SocialHandler {
  static addFaction = (
    world: WorldState,
    faction: Faction<FactionType>
  ): WorldState => {
    return {
      ...world,
      factions: [...world.factions, faction],
    };
  };

  static addFactions = (
    world: WorldState,
    factions: Faction<FactionType>[]
  ): WorldState => {
    return {
      ...world,
      factions: [...world.factions, ...factions],
    };
  };

  static isFactionMember = (
    faction: Faction<FactionType>,
    agent: FactionAgent
  ): boolean => {
    return agent.factions.findIndex((f) => f.factionId === faction.id) > -1;
  };

  static createIsFactionGroupMember =
    (factionGroup: Faction<FactionType>[]) =>
    (agent: FactionAgent): boolean => {
      const factionsIds = factionGroup.map((f) => f.id);
      return agent.factions.reduce(
        (isMember, m) => isMember || factionsIds.includes(m.factionId),
        false
      );
    };

  static SplitByFaction = <T extends FactionType, U extends FactionAgent>(
    type: T,
    factionOwners: U[],
    factionList: Faction<FactionType>[]
  ): {
    agentGroups: U[][];
    meta: MetaItem[];
    unmatched: U[];
  } => {
    const sorted: U[] = factionOwners.sort(SocialHandler.sortByFaction(type));
    const filterType = hasFactionType(type);
    const filtered: U[] = factionOwners.filter(filterType);
    const filteredFactionIds = factionList
      .filter((f) => f.type === type)
      .map((f) => f.id)
      .sort();

    const reduceToRawMetaGroups = (acc: MetaItem[][], m: MetaItem) => {
      if (acc.length === 0 && m.from < 0) {
        return acc;
      }
      return m.from < 0
        ? [...acc.slice(0, acc.length - 1), [...acc[acc.length - 1], m]]
        : [...acc, [m]];
    };

    const mapRawGroupsToCleanMeta = (rawM: MetaItem[]): MetaItem => {
      return rawM.length === 1
        ? rawM[0]
        : {
            key: rawM[0].key,
            from: rawM[0].from,
            to: rawM[rawM.length - 1].to,
            quantity: rawM[rawM.length - 1].to - rawM[0].from,
          };
    };

    const rawMeta: MetaItem[] = filteredFactionIds.map((key, i) => {
      const from = sorted.findIndex((c) =>
        c.factions.find((f) => f.factionId === filteredFactionIds[i])
      );

      const nextI: number = i + 1;
      const nextIndex = sorted.findIndex((c) =>
        c.factions.find((f) => f.factionId === filteredFactionIds[nextI])
      );
      const to: number =
        nextI < filteredFactionIds.length ? nextIndex : filtered.length; //sorted.length will include sorted element with no type, hence not included in filteredFactionIds

      return {
        key,
        from,
        to,
        quantity: to - from,
      };
    });

    const meta = rawMeta
      .reduce(reduceToRawMetaGroups, [])
      .map(mapRawGroupsToCleanMeta);

    const agentGroups: U[][] = meta.map((m) => {
      return sorted.slice(m.from, m.to);
    });

    return {
      agentGroups,
      meta,
      unmatched: sorted.slice(filtered.length, sorted.length),
    };
  };

  static sortByFaction =
    <T extends FactionType>(
      type: T,
      key: "factionId" | "factionName" | "influence" = "factionId",
      isReverse: boolean = false
    ) =>
    (fl1: FactionAgent, fl2: FactionAgent): number => {
      const f1 = fl1.factions.find((f) => f.factionType === type);
      const f2 = fl2.factions.find((f) => f.factionType === type);

      if (!isNotUndefined(f1)) {
        return 1;
      }

      if (!isNotUndefined(f2)) {
        return -1;
      }

      if (f1[key] < f2[key]) {
        return isReverse ? 1 : -1;
      }
      if (f1[key] > f2[key]) {
        return isReverse ? -1 : 1;
      }

      return 0;
    };

  static createFaction = <T extends FactionType>(
    type: T,
    name: string
  ): Faction<T> => {
    return {
      id: uuid(),
      name,
      type,
      leader: undefined,
    };
  };

  static addFactionMembership = <T extends FactionAgent, U extends FactionType>(
    agent: T,
    faction: Faction<U> | FactionMembership | FactionType,
    influence = 0
  ): T => {
    const isFactiontype = <T extends any>(
      maybeFactionType: T | FactionType
    ): maybeFactionType is FactionType => {
      return isNotUndefined(
        Object.values(FactionType).find((e) => e === maybeFactionType)
      );
    };

    const isMembership = <T extends any>(
      maybeMembership: T | FactionMembership
    ): maybeMembership is FactionMembership => {
      return (maybeMembership as FactionMembership).factionType !== undefined;
    };

    const factionType: FactionType = isFactiontype(faction)
      ? faction
      : isMembership(faction)
      ? faction.factionType
      : faction.type;

    //@TODO: HANDLE MULTIPLE FACTION OF SAME TYPE
    const [factionMatch, otherFactions] = partition(
      agent.factions,
      (f) => f.factionType === factionType
    );

    const prevMembership = factionMatch[0];

    //TODO check if name/id match
    const isMembershipMatching: boolean = (() => {
      if (isUndefined(prevMembership) || isFactiontype(faction)) {
        return false;
      }

      if (isMembership(faction)) {
        return (
          prevMembership.factionId === faction.factionId &&
          prevMembership.factionName === faction.factionName
        );
      }

      return (
        prevMembership.factionId === faction.id &&
        prevMembership.factionName === faction.name
      );
    })();

    if (isMembershipMatching) {
      //update membership
      return {
        ...agent,
        factions: [
          {
            ...prevMembership,
            influence: prevMembership.influence + influence,
          },
          ...otherFactions,
        ],
      };
    }

    //new membership
    if (isFactiontype(faction)) {
      if (!isNotUndefined(prevMembership)) {
        console.log("agent.factions", agent.factions);
        throw new Error(
          "CANT ADD MEMBERSHIP WITH JUST FACTIONTYPE WHEN NO PREV MEMEBRSHIP MATCHED"
        );
      }

      return {
        ...agent,
        factions: [
          {
            ...prevMembership,
            influence: prevMembership.influence + influence,
          },
          ...otherFactions,
        ],
      };
    }

    if (isMembership(faction)) {
      return {
        ...agent,
        factions: [
          {
            ...faction,
            influence: faction.influence + influence,
          },
          ...otherFactions,
        ],
      };
    }

    return {
      ...agent,
      factions: [
        {
          factionId: faction.id,
          factionName: faction.name,
          factionType: faction.type,
          influence,
        },
        ...otherFactions,
      ],
    };
  };

  static getFactionInfluence = (
    faction: Faction<FactionType>,
    agents: FactionAgent[]
  ): number => {
    return agents
      .map((a) => a.factions.find((f) => f.factionId === faction.id))
      .reduce((sum, m) => (m ? sum + m.influence : sum), 0);
  };

  static getFactionInfluences = (
    groupBy: FactionType,
    agents: FactionAgent[],
    factions: Faction<FactionType>[],
    sumBy?: FactionType //default is groupBy (example usage: groupBy Family and get sumBy Position)
  ): FactionMembership[] => {
    const { agentGroups } = this.SplitByFaction(groupBy, agents, factions);

    return agentGroups
      .map((agents) => {
        const groupMembership = agents[0].factions.find(
          (m) => m.factionType === groupBy
        );

        if (isUndefined(groupMembership)) {
          throw new Error("groupMembership should not be undefined");
        }

        return agents
          .map((a) =>
            a.factions.find((f) => f.factionType === (sumBy ? sumBy : groupBy))
          )
          .filter(isNotUndefined)
          .reduce(
            (sumM: FactionMembership | undefined, m) => ({
              ...groupMembership,
              influence: sumM ? sumM.influence + m.influence : m.influence,
            }),
            undefined
          );
      })
      .filter(isNotUndefined);
  };
}
